import { useEffect, useMemo, useRef } from 'react';

import { di } from 'react-magnetic-di';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import {
	type ContentContext,
	useChatContextBrowserContext,
	useChatContextStoreActions,
} from '@atlassian/conversation-assistant-store/controllers/chat-context/store';

import { useSmartLinkSubscription } from '../../../common/hooks/useSmartLinkSubscription';
import { type CurrentPageContextType } from '../../../common/ui/chat-input-action-buttons/current-page-as-context/types';
import { useCurrentPageAsContext } from '../../../common/ui/chat-input-action-buttons/current-page-as-context/useCurrentPageAsContext';
import { useCurrentPageContextEnabled } from '../../../common/ui/chat-input-action-buttons/current-page-as-context/useCurrentPageContextEnabled';
import {
	ENTITY_CONTEXT_TYPE,
	type AiFeatureContextOverride,
	type BrowserContextSanitizer,
	type CurrentEntityContext,
} from '../../../types';

import { shouldRenderContextBannerSmartCard } from './shouldRenderContextBannerSmartCard';
import { useContextBannerSmartLinkInfo } from './useContextBannerSmartLinkInfo';

type ContextLabelType = 'context' | 'referencing_text' | 'referencing_issues';

export interface ContextInfo {
	labelType: ContextLabelType;
	onDismiss: () => void;
	url: string;
	title: string;
	selectedDatabaseContent?: string[];
	selectedIssueCount?: number;
}

export interface UseActiveContextProps {
	contentContext?: ContentContext;
	currentPageAsContextStoreKey: string;
	enableBrowserContextBanner?: boolean;
	browserContextSanitizer?: BrowserContextSanitizer;
	aiFeatureContextOverride?: AiFeatureContextOverride;
	/**
	 * Called after the active context has been dismissed via the banner's
	 * "Remove context" button. Consumers use this to restore focus to a sensible
	 * location (e.g. the prompt input) once the banner unmounts, so keyboard and
	 * screen reader users are not left with focus on the removed element.
	 */
	onContextRemoved?: () => void;
}

function isValidEntityContextOverride(value: unknown): value is { url: string; name: string } {
	return (
		value != null &&
		typeof value === 'object' &&
		typeof (value as Record<string, unknown>).url === 'string' &&
		(value as Record<string, unknown>).url !== '' &&
		typeof (value as Record<string, unknown>).name === 'string' &&
		(value as Record<string, unknown>).name !== ''
	);
}

/**
 * Derives the active page/entity context for the chat input.
 * Returns a `ContextInfo` object when there is an active context to display,
 * or `null` when there is no active context (dismissed, blocked, or unavailable).
 *
 * Extracted from `ContextBanner` so that sibling components (e.g. `SpaceContextBanner`)
 * can know whether there is an active context without re-implementing the derivation logic.
 */
export function useActiveContext({
	contentContext = 'global',
	currentPageAsContextStoreKey,
	enableBrowserContextBanner = false,
	browserContextSanitizer,
	aiFeatureContextOverride,
	onContextRemoved,
}: UseActiveContextProps): ContextInfo | null {
	di(
		useCurrentPageAsContext,
		useChatContextStoreActions,
		useChatContextBrowserContext,
		useCurrentPageContextEnabled,
	);

	const { setUseCurrentPageContextUserPreference, currentContext: pageCurrentContext } =
		useCurrentPageAsContext();

	const overrideEntityContext = useMemo(() => {
		const raw = aiFeatureContextOverride?.entityContext;
		return isValidEntityContextOverride(raw) ? raw : undefined;
	}, [aiFeatureContextOverride?.entityContext]);

	const currentContext: CurrentPageContextType | CurrentEntityContext = useMemo(
		() =>
			overrideEntityContext
				? {
						type: ENTITY_CONTEXT_TYPE,
						url: overrideEntityContext.url,
						name: overrideEntityContext.name,
					}
				: pageCurrentContext,
		[overrideEntityContext, pageCurrentContext],
	);

	const { hasTarget, setAIFeatureContextDismissed } = useChatContextStoreActions();
	const [browserContext] = useChatContextBrowserContext();
	const { sendAnalyticsEvent } = useAnalytics();

	const jiraViewPayload =
		currentContext.type === 'jira_view_context' ? currentContext.payload : undefined;
	const selectedIssueCount = jiraViewPayload?.selectedIssueIdentifiers?.length;

	const isReferencingBrowserSelection =
		fg('rovo_chat_browser_selection_context') && !!browserContext?.context?.selection?.content;

	const isReferencingText =
		(currentContext.type === 'editor_context' && !!currentContext.payload?.selection?.content) ||
		(currentContext.type === 'database_context' &&
			!!currentContext.selectedElementIds &&
			fg('confluence_frontend_rovo_db_referencing_content')) ||
		isReferencingBrowserSelection;
	const isReferencingIssues = selectedIssueCount !== undefined && selectedIssueCount > 0;

	let labelType: ContextLabelType = 'context';
	if (isReferencingText) {
		labelType = 'referencing_text';
	} else if (isReferencingIssues) {
		labelType = 'referencing_issues';
	}

	const hasBrowserContext = !!browserContext?.context?.browserUrl && enableBrowserContextBanner;

	const { displayUrl, displayTitle } = useContextBannerSmartLinkInfo({
		currentContext,
		hasBrowserContext,
		browserContextUrl: browserContext?.context?.browserUrl,
	});

	const isBrowserContextBlocklisted =
		enableBrowserContextBanner &&
		browserContextSanitizer &&
		hasBrowserContext &&
		browserContext?.context?.browserUrl
			? browserContextSanitizer.isBlocklisted(browserContext.context.browserUrl)
			: false;

	const currentHostname = useMemo(() => {
		try {
			const url = new URL(displayUrl);
			return url.hostname.replace(/^www\./, '');
		} catch {
			return '';
		}
	}, [displayUrl]);

	const previousHostnameRef = useRef<string | undefined>(undefined);

	useEffect(() => {
		const previousHostname = previousHostnameRef.current;
		if (previousHostname && previousHostname !== currentHostname) {
			setUseCurrentPageContextUserPreference(currentPageAsContextStoreKey, true);
		}
		previousHostnameRef.current = currentHostname;
	}, [currentHostname, setUseCurrentPageContextUserPreference, currentPageAsContextStoreKey]);

	const useCurrentPageContextUserPreferenceValue = useCurrentPageContextEnabled(
		currentPageAsContextStoreKey,
	);

	const hasActiveEditorContext = hasBrowserContext
		? useCurrentPageContextUserPreferenceValue
		: useCurrentPageContextUserPreferenceValue &&
			(currentContext.type !== 'none' || hasTarget(contentContext));

	const isBrowserContextLoading =
		hasBrowserContext &&
		currentContext.type === 'none' &&
		labelType === 'context' &&
		!browserContext?.context?.browserUrl;

	const activeContext = useMemo((): ContextInfo | null => {
		if (
			!shouldRenderContextBannerSmartCard({
				hasActiveEditorContext,
				isBrowserContextBlocklisted,
				isBrowserContextLoading,
			})
		) {
			return null;
		}

		return {
			labelType,
			url: displayUrl,
			title: displayTitle,
			onDismiss: () => {
				sendAnalyticsEvent({
					eventType: 'ui',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'rovoChatContextBannerRemoveContextButton',
					attributes: {
						contextType: currentContext.type,
						labelType,
						hasBrowserContext,
					},
				});
				// Generic dismissal: clear any currently stored aiFeatureContext (so it
				// is not attached to the next outgoing message) AND lock the store so
				// upstream publishers
				if (
					// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
					FeatureGates.getExperimentValue(
						'jira-ai-jira-view-context-in-rovo-chat-experiment',
						'isEnabled',
						false,
					)
				) {
					setAIFeatureContextDismissed(true);
				}
				setUseCurrentPageContextUserPreference(currentPageAsContextStoreKey, false);

				// After the context is cleared the banner unmounts, which would
				// otherwise leave focus on the removed "Remove context" button and
				// strand keyboard / screen reader users. Restore focus to a sensible
				// location (the prompt input) via the consumer-provided callback.
				onContextRemoved?.();
			},
			selectedDatabaseContent:
				currentContext.type === 'database_context' ? currentContext.selectedElementIds : undefined,
			selectedIssueCount,
		};
	}, [
		hasActiveEditorContext,
		hasBrowserContext,
		isBrowserContextLoading,
		currentContext,
		labelType,
		displayUrl,
		displayTitle,
		setUseCurrentPageContextUserPreference,
		currentPageAsContextStoreKey,
		isBrowserContextBlocklisted,
		selectedIssueCount,
		sendAnalyticsEvent,
		setAIFeatureContextDismissed,
		onContextRemoved,
	]);

	// Fire the SmartLink subscription signal with the correct activation condition.
	useSmartLinkSubscription(!!hasActiveEditorContext);

	return activeContext;
}
